// src/hrms/alarms/alarmItems.ts
import type { PlannerItem } from "../api/todo";
import { closeAlarm, syncAlarmItem } from "./alarmModel";
import { dueAtFromParts } from "./alarmSchedule";
import { updateAlarm, updateAlarms } from "./alarmStore";
import { alarmItemKey, type AlarmItemKey, type AlarmItemSnapshot, type ItemAlarm } from "./alarmTypes";

/** How planner rows and tasks are described to (and keyed in) the alarm store. */

export type AlarmTrackedItem = { finished: false | "completed" | "cancelled"; key: AlarmItemKey; snapshot: AlarmItemSnapshot };

/** The fields every task shape the web has (planner row, task detail, task list) carries. */
export type AlarmTaskLike = {
  description?: string | null;
  due_date?: string | null;
  id: string;
  priority?: string | null;
  status?: string | null;
  title: string;
};

export function taskAlarmItem(task: AlarmTaskLike): AlarmTrackedItem {
  const status = String(task.status ?? "");
  return {
    finished: status === "DONE" ? "completed" : status === "CANCELLED" ? "cancelled" : false,
    key: alarmItemKey("task", task.id),
    snapshot: {
      description: task.description ?? null,
      dueAt: dueAtFromParts(task.due_date),
      id: task.id,
      priority: String(task.priority ?? "MEDIUM"),
      source: "task",
      title: task.title,
    },
  };
}

export function plannerAlarmItem(item: PlannerItem): AlarmTrackedItem {
  if (item.source === "assigned") return taskAlarmItem(item);
  return {
    finished: item.status === "COMPLETED" ? "completed" : false,
    key: alarmItemKey("todo", item.id),
    snapshot: {
      description: item.description || item.notes || null,
      dueAt: dueAtFromParts(item.due_date, item.time_of_day),
      id: item.id,
      priority: item.priority,
      source: "todo",
      title: item.title,
    },
  };
}

/**
 * Whether the viewer can put an alarm on this planner row: not on an item
 * they delegated to someone else (finishing it is the assignee's call), and
 * not on one that is already done.
 */
export function canSetAlarm(item: PlannerItem) {
  if (item.source === "personal") return item.delegated !== true && item.status !== "COMPLETED";
  return item.status !== "DONE" && item.status !== "CANCELLED";
}

/**
 * Brings the stored alarms of these items up to date with what the server
 * just returned: a moved due date moves the alarm, a finished item silences it.
 * Items without an alarm are ignored.
 */
export function syncAlarmItems(ownerId: string, items: AlarmTrackedItem[], now = Date.now()) {
  updateAlarms(ownerId, (current) => {
    let next = current;
    for (const item of items) {
      const alarm = current[item.key];
      if (!alarm) continue;
      const synced = syncAlarmItem(alarm, item.snapshot, item.finished, now);
      if (synced === alarm) continue;
      if (next === current) next = { ...current };
      next[item.key] = synced;
    }
    return next;
  });
}

/**
 * The page just finished (or deleted) an item itself: silence its alarm now,
 * rather than waiting for a reload that may no longer list the item at all.
 */
export function closeItemAlarm(ownerId: string | null, key: AlarmItemKey, reason: NonNullable<ItemAlarm["closed"]>) {
  if (!ownerId) return;
  updateAlarm(ownerId, key, (alarm) => (alarm && alarm.closed !== reason ? closeAlarm(alarm, reason) : alarm));
}
